import { Box, Stack } from '@mui/material';
import EditPanelCheckBox from 'app/editor/report/content-editor/block-edit-panel/edit-panel-components/EditPanelCheckBox';
import EditPanelLabel from 'app/editor/report/content-editor/block-edit-panel/edit-panel-components/EditPanelLabel';
import React, { useMemo } from 'react';
import type { FormDataAPIType } from 'components/common/form';
import useBundleTranslation from 'i18n';
import { TemplateFormComponent } from 'components/common/form/layout/ReactHookFormController';
import type { CalcMethodType } from '@report-content';
import type { YNValues } from 'tools/types';
import { useQuery } from '@tanstack/react-query';
import { reportAPI } from 'api/report';
import type { FormComponentValue } from 'components/common/form/layout/control';
import { buildDependency, buildDependencyRule } from 'components/common/form/dependency';

const SignMap: Record<CalcMethodType, Record<YNValues, string[]>> = {
    'fixed range': { Y: ['lt', 'gt'], N: ['gt', 'lt'] },
    'prior values': { Y: ['gt', 'gt'], N: ['gt', 'gt'] },
    'target comparison': { Y: ['gt', 'gt'], N: ['gt', 'gt'] },
};
export default function StoplightSection({
    elementProps,
    elementId,
}: {
    elementProps: FormDataAPIType;
    elementId: number;
}) {
    const { t } = useBundleTranslation(['components/report-content']);
    const includeStoplight: YNValues = elementProps.form.hookFormWatch('includeStoplight') ?? 'N';
    const stoplightCalcMethod: CalcMethodType = elementProps.form.hookFormWatch('stoplightCalcMethod') ?? 'fixed range';
    const moreIsBetter = elementProps.form.hookFormWatch('stoplightMoreIsBetter') == 'Y';

    const { data: calcMethodsData, isLoading: isLoadingCalc } = useQuery<Array<FormComponentValue>, Error>({
        queryKey: ['report_content_editor_stoplight_calc_methods'],
        queryFn: () => reportAPI.getStoplightMethods(),
    });

    const { data: targetsData, isLoading: isLoadingTargets } = useQuery<Array<FormComponentValue>, Error>({
        queryKey: ['report_content_editor_stoplight_targets', elementId],
        queryFn: () => reportAPI.getStoplightTargets(elementId),
    });

    const [lowExtraText, highExtraText] = useMemo(() => {
        const lowExtraText =
            stoplightCalcMethod == 'fixed range'
                ? ''
                : stoplightCalcMethod == 'prior values'
                  ? t('kpi_chart.below_comparison_value')
                  : t('kpi_chart.below_target');

        const highExtraText =
            stoplightCalcMethod == 'fixed range'
                ? ''
                : stoplightCalcMethod == 'prior values'
                  ? t('kpi_chart.above_comparison_value')
                  : t('kpi_chart.above_target');
        return [lowExtraText, highExtraText];
    }, [stoplightCalcMethod, t]);

    return (
        <Stack spacing={2}>
            <EditPanelCheckBox
                elementProps={elementProps}
                name={'includeStoplight'}
                label={t('kpi_chart.include_stoplight')}
            />
            <Stack spacing={2} className={includeStoplight == 'Y' ? '' : 'd-none'}>
                <EditPanelCheckBox
                    elementProps={elementProps}
                    name={'stoplightMoreIsBetter'}
                    label={t('kpi_chart.more_is_better')}
                />
                <Box>
                    <EditPanelLabel>{t('kpi_chart.stoplight_calc_method')}</EditPanelLabel>
                    <TemplateFormComponent
                        elementProps={elementProps}
                        component={'FormSelect'}
                        name={'stoplightCalcMethod'}
                        componentValues={[
                            { label: 'A fixed value', value: 'fixed range' },
                            { label: 'Prior values', value: 'prior values' },
                            { label: 'A target', value: 'target comparison' },
                        ]}
                    />
                </Box>
                <Stack direction={'row'} spacing={4}>
                    <Box flexGrow={1}>
                        <EditPanelLabel>{t('kpi_chart.stoplight_color_for_high_values')}</EditPanelLabel>
                        <TemplateFormComponent
                            elementProps={elementProps}
                            component={'ColorPicker'}
                            name={'stoplightColorForHighValues'}
                        />
                    </Box>
                    <Box flexGrow={1}>
                        <EditPanelLabel>{t('kpi_chart.stoplight_color_for_low_values')}</EditPanelLabel>
                        <TemplateFormComponent
                            elementProps={elementProps}
                            component={'ColorPicker'}
                            name={'stoplightColorForLowValues'}
                        />
                    </Box>
                </Stack>

                {!isLoadingCalc && (
                    <>
                        <Box className={stoplightCalcMethod == 'prior values' ? '' : 'd-none'}>
                            <EditPanelLabel>{t('kpi_chart.compare_value_to')}</EditPanelLabel>
                            <TemplateFormComponent
                                elementProps={elementProps}
                                component={'FormSelect'}
                                name={'stoplightCompareValueCalcMethodId'}
                                componentValues={calcMethodsData}
                            />
                        </Box>
                        <Box className={stoplightCalcMethod == 'prior values' ? '' : 'd-none'}>
                            <EditPanelLabel>{t('kpi_chart.use_value_recorded')}</EditPanelLabel>
                            <Stack direction={'row'} spacing={1}>
                                <Box sx={{ width: '80px' }}>
                                    <TemplateFormComponent
                                        elementProps={elementProps}
                                        component={'FormText'}
                                        name={'stoplightCompareValuePeriod'}
                                        label={''}
                                    />
                                </Box>
                                <Box sx={{ width: '100px' }}>
                                    <TemplateFormComponent
                                        elementProps={elementProps}
                                        component={'FormSelect'}
                                        name={'stoplightCompareValueUnit'}
                                        componentValues={[
                                            { label: 'day', value: 'day' },
                                            { label: 'week', value: 'week' },
                                            { label: 'month', value: 'month' },
                                            { label: 'year', value: 'year' },
                                        ]}
                                    />
                                </Box>
                                <Box sx={{ display: 'flex', pl: '4px' }} alignItems={'center'}>
                                    {t('kpi_chart.as_the_comparison_value')}
                                </Box>
                            </Stack>
                        </Box>
                    </>
                )}

                {!isLoadingTargets && (
                    <Box className={stoplightCalcMethod == 'target comparison' ? '' : 'd-none'}>
                        <EditPanelLabel>{t('kpi_chart.stoplight_target_id')}</EditPanelLabel>
                        <TemplateFormComponent
                            elementProps={elementProps}
                            component={'FormSelect'}
                            name={'stoplightTargetId'}
                            componentValues={targetsData}
                        />
                    </Box>
                )}

                <Box>
                    <TemplateFormComponent
                        elementProps={elementProps}
                        component={'FormCompareField'}
                        name={'stoplightOkRangeStart'}
                        label={t('kpi_chart.if_value_is')}
                        componentValues={[
                            {
                                label: SignMap[stoplightCalcMethod][moreIsBetter ? 'Y' : 'N'][0] == 'gt' ? '>' : '<',
                                value: 'strict',
                            },
                            {
                                label: SignMap[stoplightCalcMethod][moreIsBetter ? 'Y' : 'N'][0] == 'gt' ? '>=' : '<=',
                                value: 'non strict',
                            },
                        ]}
                        props={{
                            type: 'stoplightOkRangeStartComparisonType',
                            extraText: moreIsBetter ? lowExtraText : highExtraText,
                            extraTextBadge: t('kpi_chart.stoplight_is_red'),
                            variant: 'danger',
                        }}
                        deps={[
                            buildDependency([buildDependencyRule('stoplightColorForHighValues')], {
                                color: '$stoplightColorForHighValues',
                            }),
                        ]}
                    />
                </Box>
                <Box>
                    <TemplateFormComponent
                        elementProps={elementProps}
                        component={'FormCompareField'}
                        name={'stoplightOkRangeEnd'}
                        label={t('kpi_chart.if_value_is')}
                        componentValues={[
                            {
                                label: SignMap[stoplightCalcMethod][moreIsBetter ? 'Y' : 'N'][1] == 'gt' ? '>' : '<',
                                value: 'strict',
                            },
                            {
                                label: SignMap[stoplightCalcMethod][moreIsBetter ? 'Y' : 'N'][1] == 'gt' ? '>=' : '<=',
                                value: 'non strict',
                            },
                        ]}
                        props={{
                            type: 'stoplightOkRangeEndComparisonType',
                            extraText: moreIsBetter ? highExtraText : lowExtraText,
                            extraTextBadge: t('kpi_chart.stoplight_is_green'),
                            variant: 'secure',
                        }}
                        deps={[
                            buildDependency([buildDependencyRule('stoplightColorForLowValues')], {
                                color: '$stoplightColorForLowValues',
                            }),
                        ]}
                    />
                </Box>
            </Stack>
        </Stack>
    );
}
